import React, { useState, useCallback } from 'react';
import { Steps, Button, Card, Typography, Space, Alert, theme } from 'antd';
import { ComponentRegistry } from './ComponentRegistry';
import { usePropertyValues, usePageValidation } from './PropertyValueContext';
import { useComponentState } from './hooks/useComponentState';
import type { 
  PropertyUIConfig, 
  ValueChangeEvent
} from './types';

const { Step } = Steps;
const { Title, Text } = Typography;

interface PropertyWizardProps {
  config: PropertyUIConfig;
  onSave?: (values: Record<string, any>) => void;
  onCancel?: () => void;
}

export const PropertyWizard: React.FC<PropertyWizardProps> = ({
  config,
  onSave,
  onCancel
}) => {
  const { token } = theme.useToken();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  
  // Get value management from context
  const { 
    values, 
    errors, 
    pageValidation,
    handleValueChange,
    getValue,
    getComponentErrors,
    isPageValid,
    hasRequiredFieldsError,
    validateAllPages
  } = usePropertyValues();

  // Get component state management for enable/disable functionality
  const allComponents = config.pages.flatMap(page => page.components);
  const { isComponentDisabled } = useComponentState(allComponents, values);
  

  


  // Get visible pages only
  const visiblePages = config.pages.filter(page => page.visible !== false);
  const currentPage = visiblePages[currentPageIndex];

  const handleComponentChange = useCallback((event: ValueChangeEvent) => {
    handleValueChange(event);
  }, [handleValueChange]);

  const handleNext = () => {
    if (isPageValid(currentPage.id)) {
      if (currentPageIndex < visiblePages.length - 1) {
        setCurrentPageIndex(currentPageIndex + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleFinish = () => {
    if (isPageValid(currentPage.id)) {
      // Validate all pages
      const validation = validateAllPages();
      
      if (validation.isValid && onSave) {
        onSave(values);
      }
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Allow navigation to any previous step or steps with errors (so users can fix them)
    if (stepIndex <= currentPageIndex) {
      setCurrentPageIndex(stepIndex);
    } else {
      // For forward navigation, validate current page first
      if (isPageValid(currentPage.id)) {
        setCurrentPageIndex(stepIndex);
      }
    }
  };

  const getStepStatus = (stepIndex: number) => {
    const page = visiblePages[stepIndex];
    if (stepIndex === currentPageIndex) return 'process';
    
    // Check if this page has missing required fields
    const hasRequiredFieldErrors = hasRequiredFieldsError(page.id);
    
    if (pageValidation[page?.id] === false) return 'error';
    if (hasRequiredFieldErrors) return 'error';
    if (pageValidation[page?.id] === true) return 'finish';
    return 'wait';
  };

  const hasCurrentPageErrors = Object.keys(errors).some(errorKey => 
    currentPage.components.some(comp => comp.id === errorKey)
  );

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Scrollable Content Area */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto', 
        padding: '16px 8px',
        paddingBottom: '16px'
      }}>
        {/* Transform Header */}
        <div style={{ marginBottom: '16px' }}>
          <Title level={4} style={{ marginBottom: '4px' }}>
            Configure: {values.name || config.transformName}
          </Title>
          {config.description && (
            <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
              {config.description}
            </Text>
          )}
        </div>

        {/* Multi-page wizard */}
        {visiblePages.length > 1 && (
          <>
            <Steps 
              current={currentPageIndex} 
              direction="vertical"
              size="small"
              style={{ 
                marginBottom: '16px',
                fontSize: '12px'
              }}
              onChange={handleStepClick}
            >
              {visiblePages.map((page, index) => (
                <Step 
                  key={page.id}
                  title={<span style={{ fontSize: '13px' }}>{page.title}</span>}
                  status={getStepStatus(index)}
                />
              ))}
            </Steps>
            <div style={{ 
              height: '1px', 
              background: token.colorBorderSecondary, 
              marginBottom: '20px' 
            }} />
          </>
        )}

        {/* Current Page Content */}
        {currentPage && (
          <Card 
            variant="borderless"
            style={{ marginBottom: '16px' }}
            styles={{ body: { padding: '16px 12px' } }}
          >
            {currentPage.description && (
              <Text style={{ display: 'block', marginBottom: '16px', color: '#666' }}>
                {currentPage.description}
              </Text>
            )}

            {hasCurrentPageErrors && (
              <Alert
                message="Please fix the following errors:"
                type="error"
                style={{ marginBottom: '16px' }}
              />
            )}

            <Space direction="vertical" style={{ width: '100%', gap: '12px' }}>
              {currentPage.components.map(component => (
                <ComponentRegistry
                  key={component.id}
                  config={component}
                  value={getValue(component.id)}
                  onChange={handleComponentChange}
                  pageId={currentPage.id}
                  error={getComponentErrors(component.id)}
                  isDisabled={isComponentDisabled(component.id, component.disabled)}
                  allFormValues={values}
                />
              ))}
            </Space>
          </Card>
        )}
      </div>

      {/* Fixed Footer with Navigation Buttons */}
      <div style={{
        borderTop: '1px solid #f0f0f0',
        backgroundColor: token.colorBgLayout,
        padding: '12px 16px',
        flexShrink: 0
      }}>
        <Space>
          {currentPageIndex > 0 && (
            <Button onClick={handlePrevious}>
              Previous
            </Button>
          )}
          
          {currentPageIndex < visiblePages.length - 1 && (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          
          <Button type="primary" onClick={handleFinish} data-testid="properties-panel-save-button">
            Save
          </Button>
          
          {onCancel && (
            <Button onClick={onCancel} data-testid="properties-panel-cancel-button">
              Cancel
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
}; 